import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, toast } from '@kognitos/lattice';

/** Sonner toaster id for connectivity toasts (top-center). */
export const CONNECTIVITY_TOASTER_ID = 'connectivity';

/** Stable toast id for the offline connectivity notification. */
export const OFFLINE_TOAST_ID = 'network-offline';

/** Stable toast id for the back-online connectivity notification. */
export const ONLINE_TOAST_ID = 'network-online';

/** Auto-dismiss delay for the back-online toast (ms). */
const ONLINE_TOAST_DURATION_MS = 3_000;

/**
 * Listens for browser online/offline events and shows global connectivity toasts.
 * The back-online toast only appears after the offline toast was shown in the session.
 *
 * @returns {null} Renders no UI; side effects only
 */
export function NetworkConnectivityMonitor() {
  const { t } = useTranslation();
  const hasShownOfflineToastRef = useRef(false);

  useEffect(() => {
    const createCloseButton = (toastId: string) => (
      <Button variant="ghost" onClick={() => toast.dismiss(toastId)}>
        {t('common.action.close')}
      </Button>
    );

    const showOfflineToast = () => {
      if (hasShownOfflineToastRef.current) {
        return;
      }

      hasShownOfflineToastRef.current = true;
      toast.error(t('common.connectivity.offline.title'), {
        id: OFFLINE_TOAST_ID,
        toasterId: CONNECTIVITY_TOASTER_ID,
        description: t('common.connectivity.offline.description'),
        icon: (
          <Icon type="WifiOff" size="sm" className="text-muted-foreground" />
        ),
        duration: Number.POSITIVE_INFINITY,
        action: createCloseButton(OFFLINE_TOAST_ID),
      });
    };

    const showOnlineToast = () => {
      if (!hasShownOfflineToastRef.current) {
        return;
      }

      hasShownOfflineToastRef.current = false;
      toast.dismiss(OFFLINE_TOAST_ID);
      toast.success(t('common.connectivity.online.title'), {
        id: ONLINE_TOAST_ID,
        toasterId: CONNECTIVITY_TOASTER_ID,
        description: t('common.connectivity.online.description'),
        icon: <Icon type="Wifi" size="sm" className="text-success" />,
        duration: ONLINE_TOAST_DURATION_MS,
        action: createCloseButton(ONLINE_TOAST_ID),
      });
    };

    if (!navigator.onLine) {
      showOfflineToast();
    }

    window.addEventListener('offline', showOfflineToast);
    window.addEventListener('online', showOnlineToast);

    return () => {
      window.removeEventListener('offline', showOfflineToast);
      window.removeEventListener('online', showOnlineToast);
      toast.dismiss(OFFLINE_TOAST_ID);
      hasShownOfflineToastRef.current = false;
    };
  }, [t]);

  return null;
}
